import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { pmFieldClass, pmLabelClass, pmTextareaClass } from '../port-management.ui';

interface AgentFormProps {
    initialData?: any;
    onSuccess: () => void;
    onCancel: () => void;
}

export function AgentForm({ initialData, onSuccess, onCancel }: AgentFormProps) {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        defaultValues: initialData ? {
            ...initialData,
            supportedPorts: initialData.supportedPorts?.map((p: any) => p._id),
        } : {
            companyName: '',
            contactPerson: '',
            email: '',
            phone: '',
            address: '',
            supportedPorts: [],
            workingHours: '',
            status: 'Active',
        },
    });
    const toast = useToast();

    // Fetch ports for multi-select
    const { data: ports = [] } = useQuery({
        queryKey: ['ports-dropdown'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports?status=Active&limit=1000');
            const data = await res.json();
            return data.data || [];
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: any) => {
            const url = initialData ? `/api/v1/agents/${initialData._id}` : '/api/v1/agents';
            const method = initialData ? 'PUT' : 'POST';

            const res = await authenticatedFetch(url, {
                method,
                body: JSON.stringify(data),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save agent');
            }
            return res.json();
        },
        onSuccess: () => {
            toast.success(initialData ? 'Agent updated' : 'Agent created');
            onSuccess();
        },
        onError: (err: any) => {
            toast.error(err.message);
        },
    });

    const onSubmit = (data: any) => {
        mutation.mutate(data);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Company Name *</label>
                    <input
                        {...register('companyName', { required: 'Company Name is required' })}
                        className={pmFieldClass}
                    />
                    {errors.companyName && <span className="text-red-500 text-xs">{errors.companyName.message as string}</span>}
                </div>
                <div>
                    <label className={pmLabelClass}>Contact Person *</label>
                    <input
                        {...register('contactPerson', { required: 'Contact Person is required' })}
                        className={pmFieldClass}
                    />
                    {errors.contactPerson && <span className="text-red-500 text-xs">{errors.contactPerson.message as string}</span>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Email *</label>
                    <input
                        type="email"
                        {...register('email', { required: 'Email is required' })}
                        className={pmFieldClass}
                    />
                    {errors.email && <span className="text-red-500 text-xs">{errors.email.message as string}</span>}
                </div>
                <div>
                    <label className={pmLabelClass}>Phone *</label>
                    <input
                        {...register('phone', { required: 'Phone is required' })}
                        className={pmFieldClass}
                    />
                    {errors.phone && <span className="text-red-500 text-xs">{errors.phone.message as string}</span>}
                </div>
            </div>

            <div>
                <label className={pmLabelClass}>Address</label>
                <textarea
                    {...register('address')}
                    className={pmTextareaClass}
                    rows={2}
                />
            </div>

            <div>
                <label className={pmLabelClass}>Supported Ports</label>
                <select
                    multiple
                    {...register('supportedPorts')}
                    className={`${pmFieldClass} h-32`}
                >
                    {ports.map((port: any) => (
                        <option key={port._id} value={port._id}>
                            {port.name} ({port.code})
                        </option>
                    ))}
                </select>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Hold Ctrl/Cmd to select multiple ports</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Working Hours</label>
                    <input
                        {...register('workingHours')}
                        placeholder="e.g. 09:00 - 17:00"
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>Status</label>
                    <select
                        {...register('status')}
                        className={pmFieldClass}
                    >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`${erpPageBtnPrimaryClass} disabled:opacity-50`}
                >
                    {isSubmitting ? 'Saving...' : 'Save Agent'}
                </button>
            </div>
        </form>
    );
}
